import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import {
    Alert, Box, Chip, CircularProgress, InputAdornment, MenuItem, TablePagination,
    TextField, Typography,
} from '@mui/material';
import SearchIcon from '@mui/icons-material/Search';
import ActivityFilter from './ActivityFilter';
import { apiForKind, normalizeList, ontologyErrorMessage } from '../../api/ontologyApi';
import {
    OntologyKind, SCOPE_LEVEL_LABEL, codeOf, scopeLevelColor, statusColor,
} from './ontologyFormat';
import { dsVar, ink } from '../../theme/tokens';

export interface OntologyColumn {
    key: string;
    label: string;
    /** Cell content. Return a string, or a node for anything richer. */
    render: (doc: Record<string, any>) => React.ReactNode;
    width?: string;
}

export interface OntologyFilter {
    /** The query-string key, which is also the API's parameter name. */
    key: string;
    label: string;
    options: { value: string; label: string }[];
}

/**
 * Where a browser gets its rows, when they do not come from one of the three kinds.
 *
 * Added for the Documents tab. Source documents are READ-ONLY and deliberately not an
 * OntologyKind — they have no create/approve/supersede path, so apiForKind, codeOf and
 * normalizeList do not know them, and giving them a `kind` would have grown lifecycle
 * buttons that POST to routes the control plane does not serve.
 *
 * Everything else about this table — paging, the debounced search, the URL-backed filters,
 * the dim-in-place refresh, the preview-drawer row selection — is identical for a
 * document, so the table is parameterised rather than copied.
 */
export interface BrowserSource {
    /** Fetch one page. Same signature as the generated resource APIs' `list`. */
    list: (params: Record<string, any>, options: any) => Promise<any>;
    /** Unwrap this resource's collection envelope. */
    normalize: (res: any) => {
        documents: Record<string, any>[];
        pagination: { page: number; limit: number; total: number; pages: number };
    };
    /**
     * This row's identity. One field, never a fallback chain across several — a parameter
     * carries a concept_code as well as its own, and a chain previews the wrong document.
     * Null for a row with no addressable code.
     */
    codeOf: (doc: Record<string, any>) => string | null;
    /** Route prefix a row navigates to when no onRowSelect is supplied. */
    detailBase: string;
    /** Plural noun for error copy: "Could not load source documents." */
    label: string;
}

interface OntologyBrowserProps {
    /**
     * Which of the three resources this table lists. Omitted only when `source` is given,
     * which is how the Documents tab lists a resource that is deliberately not a kind.
     */
    kind?: OntologyKind;
    filters: Record<string, string>;
    columns: OntologyColumn[];
    filterControls: OntologyFilter[];
    /**
     * Placeholder for the free-text search field.
     *
     * Omit for a source whose API has no search parameter: the field would be sent as
     * `q`, silently ignored, and read to the user as a search that simply never matches.
     */
    searchPlaceholder?: string;
    emptyMessage: string;
    /**
     * Handle a row click instead of navigating to the detail page.
     *
     * Supplied by OntologyPage so a row opens the preview drawer and the list keeps its
     * scroll position, page and filters. Absent, a row navigates as before — which is what
     * keeps every existing deep link to /ontology/:kind/:code working.
     */
    onRowSelect?: (doc: Record<string, any>) => void;
    /** The code currently previewed, highlighted so the drawer's subject is obvious. */
    selectedCode?: string | null;
    /**
     * The page of documents that just loaded.
     *
     * Lets a caller fetch something per-page that the list response does not carry — the
     * Detection Controls column's in-force thresholds, which are resolved parameters rather
     * than concept fields. One request per page instead of one per row, which is what kept
     * that column off the list until now.
     */
    onDocumentsLoaded?: (documents: Record<string, any>[]) => void;
    /** Rendered to the left of the table — the Risk Library's category rail. */
    rail?: React.ReactNode;
    /** Rendered between the filter bar and the table (view toggles, explainer banners). */
    toolbar?: React.ReactNode;
    /**
     * Override where rows come from. Absent, they come from `kind` exactly as before, so
     * the three existing tabs are untouched.
     */
    source?: BrowserSource;
    /**
     * Whether the "narrow to one agent" filter applies.
     *
     * Every ontology KIND can be narrowed to an agent, so it defaults on. A source
     * document cannot: the registry has no binding to a template, and /source-documents
     * would receive an `activity` parameter it does not implement and silently ignore —
     * a filter that appears to work and does nothing.
     */
    showActivityFilter?: boolean;
}

/** Shared by all three tabs: every document carries a tier and a lifecycle status. */
export const SCOPE_LEVEL_FILTER: OntologyFilter = {
    key: 'scope_level',
    label: 'Scope',
    options: [
        { value: '', label: 'All scopes' },
        { value: 'organisation', label: 'Your organisation' },
        { value: 'regulatory', label: 'Regulatory' },
        { value: 'system', label: 'Platform' },
    ],
};

export const STATUS_FILTER: OntologyFilter = {
    key: 'status',
    label: 'Status',
    options: [
        { value: '', label: 'All statuses' },
        { value: 'approved', label: 'Approved' },
        { value: 'draft', label: 'Draft' },
    ],
};

/**
 * Write filters into the query string.
 *
 * Exported because the Risk Library's category rail and the Thresholds view toggle both
 * sit OUTSIDE the browser component; all three must write filters the same way, or the
 * rail and the filter bar would disagree about what invalidates a page.
 *
 * Returns a SETTER OF MANY. A view toggle changes `status` and `scope_level` together, and
 * two sequential single-key calls do not compose: React Router derives the functional
 * updater's argument from the current location, so the second call would be built on the
 * pre-update params and silently drop the first.
 */
export const useOntologyFilters = () => {
    const [, setSearchParams] = useSearchParams();

    return useCallback((patch: Record<string, string>) => {
        setSearchParams((current) => {
            const next = new URLSearchParams(current);
            const keys = Object.keys(patch);

            for (const key of keys) {
                if (patch[key]) next.set(key, patch[key]);
                else next.delete(key);
            }

            if (!keys.includes('page')) {
                // Any filter change invalidates the current page — page 3 of the old
                // result set is meaningless against the new one.
                next.delete('page');
                // …and the previewed row, which the new result set may not contain. A
                // drawer left open over a list that no longer holds its subject is the
                // one state this screen cannot explain.
                if (!keys.includes('selected')) next.delete('selected');
            }
            return next;
        });
    }, [setSearchParams]);
};

/** The single-key form, for the filter bar's own controls. */
export const useOntologyFilter = () => {
    const setFilters = useOntologyFilters();
    return useCallback(
        (key: string, value: string) => setFilters({ [key]: value }),
        [setFilters],
    );
};

export const ScopeLevelChip: React.FC<{ scopeLevel?: string }> = ({ scopeLevel }) => (
    <Chip
        size="small"
        label={SCOPE_LEVEL_LABEL[scopeLevel ?? ''] ?? scopeLevel ?? '—'}
        sx={{
            height: 20,
            fontSize: '0.6875rem',
            fontWeight: 500,
            color: ink(scopeLevelColor(scopeLevel)),
            backgroundColor: 'transparent',
            border: `1px solid ${scopeLevelColor(scopeLevel)}`,
        }}
    />
);

export const StatusChip: React.FC<{ status?: string }> = ({ status }) => (
    <Chip
        size="small"
        label={status === 'approved' ? 'Approved' : 'Draft'}
        sx={{
            height: 20,
            fontSize: '0.6875rem',
            fontWeight: 500,
            color: ink(statusColor(status)),
            backgroundColor: 'transparent',
            border: `1px solid ${statusColor(status)}`,
        }}
    />
);

const CODE_SX = {
    fontFamily: '"JetBrains Mono", monospace',
    fontSize: '0.75rem',
    color: dsVar('fg-2'),
};

/**
 * Browse one ontology collection.
 *
 * Paging and filtering are both SERVER-side: these collections are shared across tenants
 * and only the API applies the scope discriminator that keeps one institution's
 * thresholds away from another. Fetching everything and filtering in the browser would
 * work for 262 concepts and be the wrong shape for the one that matters.
 *
 * Filters live in the query string rather than component state so a filtered view is
 * linkable — which is what lets the agents drawer send someone straight to the pending
 * drafts for one activity.
 */
const OntologyBrowser: React.FC<OntologyBrowserProps> = ({
    kind, filters, columns, filterControls, searchPlaceholder, emptyMessage,
    onRowSelect, selectedCode, onDocumentsLoaded, rail, toolbar,
    source, showActivityFilter = true,
}) => {
    const navigate = useNavigate();
    const setFilter = useOntologyFilter();

    // The three kinds keep their existing behaviour by DEFAULT rather than by a branch, so
    // there is one code path through the table and no way for the two to drift.
    const rows: BrowserSource | null = useMemo(() => {
        if (source) return source;
        // Every caller supplies one or the other. A `kind` here is always one of the
        // three, so apiForKind cannot return null for it.
        if (!kind) return null;
        return {
            list: (params, options) => apiForKind(kind)!.list(params, options),
            normalize: (res) => normalizeList(res, kind),
            codeOf: (doc) => codeOf(doc, kind),
            detailBase: `/ontology/${kind}`,
            label: kind,
        };
    }, [source, kind]);

    const [documents, setDocuments] = useState<Record<string, any>[]>([]);
    const [pagination, setPagination] = useState({ page: 1, limit: 25, total: 0, pages: 1 });
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState<string | null>(null);

    // The text input is local so typing is not debounced through the URL; the committed
    // value in `filters.q` is what actually drives the query.
    const [searchInput, setSearchInput] = useState(filters.q ?? '');
    useEffect(() => { setSearchInput(filters.q ?? ''); }, [filters.q]);

    const page = Number(filters.page) || 1;

    /**
     * The query-string keys that are NOT list filters.
     *
     * `selected` is view state — which row the preview drawer is describing. It travels in
     * the URL so a preview is linkable, but it must never reach api.list(): it would be
     * sent as an unknown parameter AND, worse, change filterKey, so opening the drawer
     * would refetch and dim the very list the drawer exists to keep still.
     */
    const queryFilters = useMemo(() => {
        const { selected, ...rest } = filters;
        return rest;
    }, [filters]);

    // Serialised so the effect below re-runs on a filter change without depending on an
    // object identity that changes every render.
    const filterKey = JSON.stringify(queryFilters);

    // The callback is deliberately NOT an effect dependency: it fires as a consequence of
    // a load, never as a reason for one.
    const onDocumentsLoadedRef = useRef(onDocumentsLoaded);
    useEffect(() => { onDocumentsLoadedRef.current = onDocumentsLoaded; }, [onDocumentsLoaded]);

    useEffect(() => {
        if (!rows) return undefined;

        const controller = new AbortController();
        let active = true;

        (async () => {
            setLoading(true);
            setError(null);
            try {
                const params = { ...queryFilters, page };
                const res = await rows.list(params, { signal: controller.signal });
                if (!active) return;
                const { documents: docs, pagination: p } = rows.normalize(res);
                setDocuments(docs);
                setPagination(p);
                // Read off a ref rather than the prop, so a caller passing an inline
                // arrow does not have to memoise it to avoid refetching the whole list.
                onDocumentsLoadedRef.current?.(docs);
            } catch (err: any) {
                if (!active || err?.name === 'CanceledError' || err?.code === 'ERR_CANCELED') return;
                console.error(`Error loading ${rows.label}:`, err);
                setError(ontologyErrorMessage(err, `Could not load ${rows.label}.`));
                setDocuments([]);
            } finally {
                if (active) setLoading(false);
            }
        })();

        return () => { active = false; controller.abort(); };
    }, [rows, filterKey, page]); // eslint-disable-line react-hooks/exhaustive-deps

    const debouncedSearch = useMemo(() => {
        let timer: ReturnType<typeof setTimeout>;
        return (value: string) => {
            clearTimeout(timer);
            timer = setTimeout(() => setFilter('q', value), 300);
        };
    }, [setFilter]);

    const openDetail = (doc: Record<string, any>) => {
        // By kind, not by a fallback chain across the three code fields: a parameter
        // carries a concept_code too, and a chain would navigate to the concept's code
        // under /parameters/ — a document that does not exist.
        if (!rows) return;
        const code = rows.codeOf(doc);
        if (!code) return;
        // A preview handler takes precedence: the drawer keeps the list on screen, which
        // is the whole point of it. Without one this navigates, as it always has.
        if (onRowSelect) onRowSelect(doc);
        else navigate(`${rows.detailBase}/${encodeURIComponent(code)}`);
    };

    // The first load has nothing to show, so it gets a spinner. Every load AFTER that has
    // the previous rows on screen, and replacing them with a spinner makes the table flash
    // on every keystroke, filter change and page turn. Dim them in place instead: the
    // shape of the answer stays visible while the new one arrives.
    const isInitialLoad = loading && documents.length === 0;
    const isRefreshing = loading && documents.length > 0;

    return (
        <Box>
            {/* ── Filter bar ── */}
            <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 1.5, mb: 2.5, alignItems: 'center' }}>
                {searchPlaceholder && (
                    <TextField
                        size="small"
                        placeholder={searchPlaceholder}
                        value={searchInput}
                        onChange={(e) => { setSearchInput(e.target.value); debouncedSearch(e.target.value); }}
                        InputProps={{
                            startAdornment: (
                                <InputAdornment position="start">
                                    <SearchIcon sx={{ fontSize: 18, color: dsVar('fg-muted') }} />
                                </InputAdornment>
                            ),
                        }}
                        sx={{
                            minWidth: 280,
                            '& .MuiOutlinedInput-root': {
                                backgroundColor: dsVar('surface'),
                                color: dsVar('fg-body'),
                                fontSize: '0.875rem',
                                '& fieldset': { borderColor: dsVar('line') },
                                '&:hover fieldset': { borderColor: dsVar('line') },
                                '&.Mui-focused fieldset': { borderColor: dsVar('accent-fg') },
                            },
                        }}
                    />
                )}

                {/* Every kind can be narrowed to one agent: concepts it cites, policies
                    governing those concepts, parameters configuring it. Shared here
                    rather than declared per-tab so the three cannot drift. */}
                {showActivityFilter && (
                    <ActivityFilter
                        value={filters.activity ?? ''}
                        onChange={(code) => setFilter('activity', code)}
                    />
                )}

                {filterControls.map((control) => (
                    <TextField
                        key={control.key}
                        select
                        size="small"
                        label={control.label}
                        value={filters[control.key] ?? ''}
                        onChange={(e) => setFilter(control.key, e.target.value)}
                        sx={{
                            minWidth: 168,
                            '& .MuiInputLabel-root': { color: dsVar('fg-2'), fontSize: '0.8125rem' },
                            '& .MuiInputLabel-root.Mui-focused': { color: dsVar('accent-fg') },
                            '& .MuiOutlinedInput-root': {
                                backgroundColor: dsVar('surface'),
                                color: dsVar('fg-body'),
                                fontSize: '0.875rem',
                                '& fieldset': { borderColor: dsVar('line') },
                                '&:hover fieldset': { borderColor: dsVar('line') },
                                '&.Mui-focused fieldset': { borderColor: dsVar('accent-fg') },
                            },
                            '& .MuiSvgIcon-root': { color: dsVar('fg-muted') },
                        }}
                    >
                        {control.options.map((option) => (
                            <MenuItem key={option.value} value={option.value} sx={{ fontSize: '0.875rem' }}>
                                {option.label}
                            </MenuItem>
                        ))}
                    </TextField>
                ))}
            </Box>

            {error && (
                <Alert severity="error" onClose={() => setError(null)} sx={{ mb: 2 }}>
                    {error}
                </Alert>
            )}

            {toolbar}

            {/* ── Rail + table ── */}
            <Box sx={{ display: 'flex', gap: 3, alignItems: 'flex-start' }}>
                {rail}

                {/* minWidth: 0 lets the table's own horizontal scroll work inside a flex
                    child rather than forcing the whole row wider than the page. */}
                <Box sx={{ flex: 1, minWidth: 0 }}>
                    <Box sx={{
                        border: `1px solid ${dsVar('line')}`,
                        borderRadius: '8px',
                        backgroundColor: dsVar('surface'),
                        overflowX: 'auto',
                        // Dimmed rather than blanked while refreshing, and inert so a click cannot
                        // land on a row that is about to be replaced.
                        opacity: isRefreshing ? 0.45 : 1,
                        pointerEvents: isRefreshing ? 'none' : 'auto',
                        transition: 'opacity 120ms ease-out',
                    }}>
                        <Box component="table" sx={{ width: '100%', minWidth: 720, borderCollapse: 'collapse' }}>
                            <Box component="thead">
                                <Box component="tr" sx={{ borderBottom: `1px solid ${dsVar('line')}` }}>
                                    {columns.map((column) => (
                                        <Box
                                            key={column.key}
                                            component="th"
                                            sx={{
                                                textAlign: 'left',
                                                px: 2, py: 1.5,
                                                width: column.width,
                                                fontSize: '0.6875rem',
                                                fontWeight: 600,
                                                letterSpacing: '0.05em',
                                                textTransform: 'uppercase',
                                                color: dsVar('fg-muted'),
                                            }}
                                        >
                                            {column.label}
                                        </Box>
                                    ))}
                                </Box>
                            </Box>

                            <Box component="tbody">
                                {isInitialLoad && (
                                    <Box component="tr">
                                        <Box component="td" colSpan={columns.length} sx={{ p: 6, textAlign: 'center' }}>
                                            <CircularProgress size={24} sx={{ color: dsVar('accent-fg') }} />
                                        </Box>
                                    </Box>
                                )}

                                {!loading && documents.length === 0 && (
                                    <Box component="tr">
                                        <Box
                                            component="td"
                                            colSpan={columns.length}
                                            sx={{ p: 6, textAlign: 'center', color: dsVar('fg-muted'), fontSize: '0.875rem' }}
                                        >
                                            {emptyMessage}
                                        </Box>
                                    </Box>
                                )}

                                {documents.map((doc, index) => {
                                    const code = rows?.codeOf(doc);
                                    const isSelected = Boolean(selectedCode) && code === selectedCode;
                                    return (
                                        <Box
                                            key={`${code}-${doc.status}-${index}`}
                                            component="tr"
                                            onClick={() => openDetail(doc)}
                                            sx={{
                                                borderBottom: index === documents.length - 1 ? 'none' : `1px solid ${dsVar('line')}`,
                                                cursor: 'pointer',
                                                // The previewed row stays marked while the drawer is
                                                // open, so it is never ambiguous which one it describes.
                                                backgroundColor: isSelected ? dsVar('raised') : 'transparent',
                                                boxShadow: isSelected ? `inset 2px 0 0 ${dsVar('accent-fg')}` : 'none',
                                                '&:hover': { backgroundColor: dsVar('raised') },
                                            }}
                                        >
                                            {columns.map((column) => (
                                                <Box
                                                    key={column.key}
                                                    component="td"
                                                    sx={{ px: 2, py: 1.75, fontSize: '0.875rem', color: dsVar('fg-soft'), verticalAlign: 'top' }}
                                                >
                                                    {column.render(doc)}
                                                </Box>
                                            ))}
                                        </Box>
                                    );
                                })}
                            </Box>
                        </Box>
                    </Box>

                    <TablePagination
                        component="div"
                        count={pagination.total}
                        page={Math.max(pagination.page - 1, 0)}
                        rowsPerPage={pagination.limit}
                        // The API clamps limit to [1, 500]; these are the useful steps below that.
                        rowsPerPageOptions={[25, 50, 100]}
                        onPageChange={(_e, nextPage) => setFilter('page', String(nextPage + 1))}
                        onRowsPerPageChange={(e) => setFilter('limit', e.target.value)}
                        sx={{
                            color: dsVar('fg-2'),
                            '& .MuiTablePagination-selectIcon': { color: dsVar('fg-muted') },
                            '& .MuiIconButton-root.Mui-disabled': { color: dsVar('fg-faint') },
                            '& .MuiIconButton-root': { color: dsVar('fg-2') },
                        }}
                    />
                </Box>
            </Box>
        </Box>
    );
};

export const CodeText: React.FC<{ children: React.ReactNode }> = ({ children }) => (
    <Typography component="span" sx={CODE_SX}>{children}</Typography>
);

export default OntologyBrowser;
